<template>
  <div
    class="sms-page"
    @pointerenter="smsPointerInside = true"
    @pointerleave="smsPointerInside = false"
  >
    <div class="title-bar">
      <button
        type="button"
        class="title-button"
        title="New message"
        @click="openSearch"
      >
        <i class="fas fa-plus"></i>
      </button>
      <span>SMS Messaging</span>
      <div class="title-buttons">
        <button type="button" class="title-button" @click="$router.go(-1)">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button
          type="button"
          class="title-button"
          title="Refresh"
          :disabled="refreshing"
          @click="manualRefresh"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
        </button>
        <button
          v-if="isEmbedded"
          type="button"
          class="title-button"
          title="Close"
          @click="sendClose"
        >
          <i class="fas fa-close"></i>
        </button>
        <button
          type="button"
          class="title-button"
          title="Home"
          @click="$router.push('/mhome')"
        >
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>

    <div class="sms-hint">
      <span>last 14 days shown.</span>
      <button
        type="button"
        class="sms-search-all"
        :class="{ 'sms-search-all-on': searchAll }"
        :aria-pressed="searchAll ? 'true' : 'false'"
        @click="toggleSearchAll"
      >
        search all
      </button>
    </div>

    <div v-if="partyFilter" class="sms-filter-bar">
      <span class="sms-filter-label">{{ partyFilterLabel }}</span>
      <button
        type="button"
        class="sms-filter-clear"
        title="Clear filter"
        @click="clearPartyFilter"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="sms-list-filter">
      <div class="sms-list-filter-wrap">
        <input
          v-model="listFilterInput"
          class="sms-list-filter-input"
          type="text"
          placeholder="Filter by message, name, or address"
          @input="onListFilterInput"
        />
        <button
          v-if="listFilterInput || listQuery"
          type="button"
          class="sms-list-filter-clear"
          title="Clear filter"
          @click="clearListFilter"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div ref="smsList" class="sms-list" @click="onListClick">
      <div v-if="loading" class="sms-status">Loading…</div>
      <div v-else-if="loadError" class="sms-status sms-error">{{ loadError }}</div>
      <div v-else-if="searchError" class="sms-status sms-error">{{ searchError }}</div>
      <div v-else-if="searchingAll && !searchInbox.length" class="sms-status">
        Searching…
      </div>
      <div v-else-if="!inbox.length && !(searchAll && listQuery.trim())" class="sms-status">
        No incoming texts.
      </div>
      <div v-else-if="!visibleInbox.length" class="sms-status">
        {{ listQuery.trim() ? 'No matches.' : 'No incoming texts from this person.' }}
      </div>
      <template v-else>
        <div
          v-for="group in groupedInbox"
          :key="group.key"
          class="sms-group"
          :class="{ 'sms-group-older': group.key === 'older' }"
        >
          <button
            type="button"
            class="sms-group-header"
            :aria-expanded="!collapsed[group.key]"
            @click="toggleGroup(group.key)"
          >
            <i
              :class="
                collapsed[group.key]
                  ? 'fas fa-chevron-right'
                  : 'fas fa-chevron-down'
              "
            ></i>
            <span>{{ group.label }}</span>
            <span class="sms-group-count">{{ group.items.length }}</span>
          </button>
          <div v-show="!collapsed[group.key]">
            <div
              v-for="msg in group.items"
              :key="msg.id"
              class="sms-item"
              :data-push-id="msg.id"
              :class="{
                'sms-item-selected': selectedId === msg.id,
                'sms-item-out': msg.direction === 'out',
                'sms-item-reply': smsInboxIsReply(msg),
                'sms-item-push-highlight': highlightId === msg.id,
              }"
              @click.stop="selectMessage(msg, $event)"
              @dblclick.stop="restoreFailedToInput(msg)"
              @pointerdown="onItemPointerDown(msg, $event)"
              @pointerup="onItemPointerUp"
              @pointercancel="onItemPointerUp"
            >
              <div class="sms-stack">
                <div class="sms-item-top">
                  <span class="sms-from-row">
                    <span
                      class="sms-from"
                      :class="{ 'sms-from-active': !!partyFilter }"
                      @click.stop="filterByParty(msg)"
                    >
                      <template v-if="unknownSmsContactTarget(msg)">
                        {{ unknownSmsContactTarget(msg).prefix }}unknown
                      </template>
                      <template v-else>{{ msg.header }}</template>
                    </span>
                    <button
                      v-if="unknownSmsContactTarget(msg)"
                      type="button"
                      class="sms-create-contact"
                      @click.stop="openUnknownContact(msg)"
                    >
                      create contact
                    </button>
                    <button
                      v-else-if="externalSmsContactTarget(msg)"
                      type="button"
                      class="sms-create-contact"
                      @click.stop="openExternalEdit(msg)"
                    >
                      edit
                    </button>
                  </span>
                  <span class="sms-ago">{{
                    stamp(msg.createdAt, expandedId === msg.id)
                  }}</span>
                </div>
                <div class="sms-bubble">
                  <div v-if="msg.inReplyTo" class="sms-in-reply">
                    (in reply to) {{ replyPreview(msg.inReplyTo) }}
                  </div>
                  <div
                    v-if="msg.body"
                    class="sms-body"
                    :class="{ 'sms-body-expanded': expandedId === msg.id }"
                  >
                    {{ msg.body }}
                  </div>
                  <template v-for="item in msg.media || []" :key="item.id || item.previewUrl">
                    <img
                      v-if="smsMediaUrls[item.id] || item.previewUrl"
                      class="sms-media"
                      :src="smsMediaUrls[item.id] || item.previewUrl"
                      alt="Photo"
                    />
                  </template>
                  <div v-if="msg.pendingOptIn" class="sms-pending-opt-in">
                    pending opt-in
                  </div>
                  <div v-else-if="scheduledNotice(msg)" class="sms-scheduled">
                    {{ scheduledNotice(msg) }}
                  </div>
                  <span
                    v-if="sendDot(msg)"
                    class="sms-send-dot"
                    :class="'sms-send-dot-' + sendDot(msg)"
                    :aria-label="sendDot(msg)"
                  ></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-if="retryMessage" class="sms-retry-bar">
      <span class="sms-retry-label">
        {{
          retryMessage.pendingOptIn ? "Pending opt-in selected" : "Message selected"
        }}
        <span v-if="sendError" class="sms-error">{{ sendError }}</span>
      </span>
      <span class="sms-retry-actions">
        <button
          v-if="retryMessage.sendFailed"
          type="button"
          class="sms-send sms-delete"
          :disabled="deleting || retrying"
          @click="deleteSelected"
        >
          {{ deleting ? "Deleting…" : "Delete" }}
        </button>
        <button
          type="button"
          class="sms-send"
          :disabled="retrying || deleting"
          @click="retrySelected"
        >
          {{ retrying ? "Retrying…" : "Retry" }}
        </button>
      </span>
    </div>

    <div v-if="composeTarget" class="sms-compose">
      <div class="sms-compose-to">
        <span class="sms-compose-to-label">
          To: {{ composeTarget.label }}
          <span v-if="composeOptInOnce" class="sms-basic-once">
            · Basic opt-in for this message
          </span>
        </span>
        <button
          type="button"
          class="sms-opt-in-edit"
          title="Edit first-time opt-in message"
          aria-label="Edit first-time opt-in message"
          @click="openOptInGateEditor"
        >
          <i class="fas fa-pen"></i>
        </button>
        <button
          type="button"
          class="sms-compose-close"
          title="Close"
          aria-label="Close compose"
          @click="closeCompose"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <textarea
        v-model="replyBody"
        class="sms-compose-input"
        rows="3"
        maxlength="1600"
        placeholder="Reply…"
        @keydown="onComposeKeydown"
      ></textarea>
      <div v-if="composePhoto" class="sms-photo-preview">
        <img :src="composePhoto.url" alt="Photo to send" />
        <button
          type="button"
          class="sms-photo-remove"
          title="Remove photo"
          aria-label="Remove photo"
          @click="clearComposePhoto"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="sms-compose-actions">
        <div class="sms-photo-tools">
          <label
            class="sms-photo-btn"
            title="Gallery"
            aria-label="Choose a photo from the gallery"
            :class="{ 'sms-photo-btn-off': sending || composePhotoBusy }"
          >
            <input
              class="sms-photo-input"
              type="file"
              accept="image/*"
              :disabled="sending || composePhotoBusy"
              @change="onComposePhoto"
            />
            <i class="fas fa-images"></i>
          </label>
          <label
            class="sms-photo-btn"
            title="Camera"
            aria-label="Take a photo with the camera"
            :class="{ 'sms-photo-btn-off': sending || composePhotoBusy }"
          >
            <input
              class="sms-photo-input"
              type="file"
              accept="image/*"
              capture="environment"
              :disabled="sending || composePhotoBusy"
              @change="onComposePhoto"
            />
            <i class="fas fa-camera"></i>
          </label>
        </div>
        <span v-if="sendError" class="sms-error">{{ sendError }}</span>
        <button
          type="button"
          class="sms-send"
          :disabled="sending || composePhotoBusy || (!replyBody.trim() && !composePhoto)"
          title="Send now. Press and hold to choose a later time."
          @pointerdown="onSendPointerDown"
          @pointermove="onSendPointerMove"
          @pointerup="onSendPointerEnd"
          @pointercancel="onSendPointerEnd"
          @contextmenu.prevent
          @click="onSendClick"
        >
          <i class="fas fa-clock sms-send-clock" aria-hidden="true"></i>
          Send
        </button>
      </div>
    </div>

    <div
      v-if="showSchedule"
      class="sms-opt-in-overlay"
      @click.self="closeSchedule"
    >
      <div class="sms-opt-in-dialog" role="dialog" aria-label="Schedule SMS">
        <p>Send this message later.</p>
        <input
          v-model="scheduleLocal"
          class="sms-schedule-input"
          type="datetime-local"
          :min="scheduleMin"
        />
        <span v-if="scheduleError" class="sms-error">{{ scheduleError }}</span>
        <div class="sms-opt-in-actions">
          <button type="button" class="sms-opt-in-cancel" @click="closeSchedule">
            Cancel
          </button>
          <button type="button" class="sms-send" @click="confirmSchedule">
            Schedule
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showOptInGateEditor"
      class="sms-opt-in-overlay"
      @click.self="closeOptInGateEditor"
    >
      <div
        class="sms-opt-in-dialog sms-opt-in-gate-dialog"
        role="dialog"
        aria-label="First-time opt-in message"
      >
        <p>
          This is sent the first time someone has not opted in. Save changes
          the default for everyone. Basic applies only to the message you are
          about to send.
        </p>
        <textarea
          v-model="optInGateDraft"
          class="sms-opt-in-gate-input"
          rows="8"
          maxlength="1600"
        ></textarea>
        <span v-if="optInGateError" class="sms-error">{{ optInGateError }}</span>
        <div class="sms-opt-in-actions">
          <button
            type="button"
            class="sms-opt-in-basic"
            @click="useBasicOptInGate"
          >
            Basic
          </button>
          <div class="sms-opt-in-actions-right">
            <button
              type="button"
              class="sms-send"
              :disabled="optInGateSaving || !optInGateDraft.trim()"
              @click="saveOptInGate"
            >
              {{ optInGateSaving ? "Saving…" : "Save" }}
            </button>
            <button
              type="button"
              class="sms-opt-in-cancel"
              @click="closeOptInGateEditor"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showSearch"
      class="sms-search-overlay"
      @click.self="closeSearch"
    >
      <div class="sms-search-popup" @click.stop>
        <div class="sms-search-title">
          <span>New message</span>
          <button
            type="button"
            class="sms-search-close"
            title="Close"
            @click="closeSearch"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <input
          ref="searchInput"
          v-model="searchQuery"
          class="sms-search-input"
          type="text"
          placeholder="Search name, property, or number"
          @input="onSearchInput"
        />
        <div class="sms-search-results">
          <div v-if="searching" class="sms-status">Searching…</div>
          <button
            v-for="(hit, hitIndex) in searchHits"
            :key="hit.kind + '-' + (hit.peopleId || hit.userId || hit.phoneDigits || hit.phone)"
            type="button"
            class="sms-search-hit"
            :class="{ 'sms-search-hit-alt': hitIndex % 2 === 1 }"
            @click="pickRecipient(hit)"
          >
            <span class="sms-search-hit-name">{{ hit.name }}</span>
            <span class="sms-search-hit-side">
              <span class="sms-search-hit-meta">
                {{
                  hit.kind === 'staff'
                    ? 'Staff'
                    : hit.kind === 'external'
                      ? 'External contact'
                      : 'Tenant'
                }}
                <template v-if="hit.property"> · {{ hit.property }}</template>
              </span>
              <span
                class="sms-opt-dot"
                :class="hit.optedIn ? 'sms-opt-dot-in' : 'sms-opt-dot-empty'"
                :aria-label="hit.optedIn ? 'opted in' : 'not opted in'"
              ></span>
            </span>
          </button>
          <div v-if="newNumberOption" class="sms-new-number">
            <div class="sms-new-number-title">
              New number {{ newNumberOption.phone }}
            </div>
            <input
              v-model="externalNameInput"
              class="sms-new-number-name"
              type="text"
              placeholder="Name (optional)"
            />
            <button
              type="button"
              class="sms-search-hit"
              @click="pickNewNumber"
            >
              <span class="sms-search-hit-name">Use this number</span>
              <span class="sms-search-hit-side">
                <span class="sms-search-hit-meta">External contact</span>
                <span
                  class="sms-opt-dot sms-opt-dot-empty"
                  aria-label="not opted in"
                ></span>
              </span>
            </button>
          </div>
          <div v-if="recipientSearchStatus" class="sms-status">
            {{ recipientSearchStatus }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="optInPopup" class="sms-opt-in-overlay" @click.self="optInPopup = ''">
      <div class="sms-opt-in-dialog" role="alertdialog">
        <p>{{ optInPopup }}</p>
        <button type="button" class="sms-opt-in-ok" @click="optInPopup = ''">
          OK
        </button>
      </div>
    </div>

    <div
      v-if="unknownContact"
      class="sms-opt-in-overlay"
      @click.self="closeUnknownContact"
    >
      <div
        class="sms-opt-in-dialog"
        role="dialog"
        aria-label="External contact"
      >
        <p>{{ unknownContactPrompt }}</p>
        <input
          ref="unknownContactInput"
          v-model="unknownContactName"
          class="sms-unknown-name-input"
          type="text"
          maxlength="128"
          placeholder="Name"
          @keydown.enter.prevent="saveUnknownContact"
        />
        <span v-if="unknownContactError" class="sms-error">{{
          unknownContactError
        }}</span>
        <div class="sms-opt-in-actions">
          <span></span>
          <div class="sms-opt-in-actions-right">
            <button
              type="button"
              class="sms-send"
              :disabled="unknownContactSaving || !unknownContactName.trim()"
              @click="saveUnknownContact"
            >
              {{ unknownContactSaving ? "Saving…" : "Save" }}
            </button>
            <button
              type="button"
              class="sms-opt-in-cancel"
              :disabled="unknownContactSaving"
              @click="closeUnknownContact"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="externalEdit"
      class="sms-opt-in-overlay"
      @click.self="closeExternalEdit"
    >
      <div
        class="sms-opt-in-dialog"
        role="dialog"
        aria-label="Edit external contact"
      >
        <p>Edit contact</p>
        <label class="sms-edit-field">
          Phone
          <input
            ref="externalEditPhoneInput"
            v-model="externalEditPhone"
            class="sms-unknown-name-input"
            type="tel"
            maxlength="20"
            :disabled="externalEditSaving"
            @keydown.enter.prevent="saveExternalEdit"
            @keydown.esc.prevent="closeExternalEdit"
          />
        </label>
        <label class="sms-edit-field">
          Name
          <input
            v-model="externalEditName"
            class="sms-unknown-name-input"
            type="text"
            maxlength="128"
            :disabled="externalEditSaving"
            @keydown.enter.prevent="saveExternalEdit"
            @keydown.esc.prevent="closeExternalEdit"
          />
        </label>
        <label class="sms-edit-optin">
          <input
            v-model="externalEditOptIn"
            type="checkbox"
            :disabled="externalEditSaving || !externalEditReady"
          />
          Opt in to text alerts
        </label>
        <span v-if="externalEditError" class="sms-error">{{
          externalEditError
        }}</span>
        <div class="sms-opt-in-actions">
          <span></span>
          <div class="sms-opt-in-actions-right">
            <button
              type="button"
              class="sms-send"
              :disabled="!externalEditCanSave"
              @click="saveExternalEdit"
            >
              {{ externalEditSaving ? "Saving…" : "Save" }}
            </button>
            <button
              type="button"
              class="sms-opt-in-cancel"
              :disabled="externalEditSaving"
              @click="closeExternalEdit"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import io from 'socket.io-client';
import {
  formatSmsInboxStamp,
  inReplyToPreview,
  smsInboxIsReply,
  mergeSmsInboxMessages,
  groupSmsInboxByDate,
  filterSmsInboxByParty,
  filterSmsInboxSearch,
  keepRecentSmsInbox,
  readSmsInboxCache,
  writeSmsInboxCache,
  nextSmsInboxPartyFilter,
  composeTargetFromPartyFilter,
  smsComposeCtrlEnter,
  smsInboxEscapeAction,
  smsInboxHotkey,
  smsInboxLastSender,
  smsInboxIsEmbedded,
  smsNewNumberOption,
  smsRecipientSearchStatus,
  smsInboxHeader,
  formatSmsPhone,
  unknownSmsContactTarget,
  externalSmsContactTarget,
  externalContactEditError,
  applyExternalContactToInbox,
  applyExternalContactEditToInbox,
  smsSelectionIsText,
  parseSmsSearchPhone,
  smsPendingOptInPopup,
  smsInboxSendDot,
  smsCanRetryPending,
  smsBodyIsClipped,
  smsSelectAction,
  composeRestoreFromFailed,
  optimisticOutboundMessage,
  dropInboxMessageById,
  smsRefreshDelay,
  smsInboxActivityKey,
  nextSmsRefreshStep,
} from '@/utils/smsInbox';
import {
  scheduledSmsNotice,
  sendAtFromLocalInput,
  defaultScheduleLocalValue,
  SMS_SEND_HOLD_MS,
  SMS_SEND_HOLD_MOVE_PX,
} from '@/utils/smsSchedule';
import { smsPhotoToJpeg, blobToJpegDataUrl } from '@/utils/smsPhotoJpeg';
import { smsTabIsVisible } from '@/utils/smsSeen';
import {
  loadSmsMediaObjectUrls,
  revokeSmsMediaUrls,
} from '@/utils/smsMedia';
import {
  getCachedSmsPhoto,
  putCachedSmsPhoto,
  trimCachedSmsPhotos,
  smsPhotoIsWithinDays,
  smsMediaMessageTimes,
} from '@/utils/smsMediaCache';
import eventBus from './eventBus.js';
import { dateBucket, dateGroupCollapsedByDefault } from '@/utils/dateGroups';
import { pushHighlightPlan } from '@/utils/pushOpenTarget';
import {
  DEFAULT_OPT_IN_GATE_BODY,
  BASIC_OPT_IN_GATE_BODY,
  optInGateEditorText,
} from '@/utils/smsOptInGate';

export default {
  name: 'MSmsInbox',
  data() {
    return {
      inbox: [],
      smsMediaUrls: {},
      loading: false,
      loadError: '',
      selectedId: null,
      expandedId: null,
      composeTarget: null,
      replyBody: '',
      composePhoto: null,
      composePhotoBusy: false,
      sending: false,
      sendError: '',
      showSearch: false,
      searchQuery: '',
      searchHits: [],
      searchFailed: false,
      searching: false,
      searchTimer: null,
      clock: Date.now(),
      clockTimer: null,
      socket: null,
      refreshing: false,
      partyFilter: null,
      partyFilterLabel: '',
      listFilterInput: '',
      listQuery: '',
      listFilterTimer: null,
      smsPointerInside: false,
      searchAll: false,
      searchInbox: [],
      searchingAll: false,
      searchError: '',
      longPressTimer: null,
      skipNextSelect: false,
      externalNameInput: '',
      unknownContact: null,
      unknownContactName: '',
      unknownContactError: '',
      unknownContactSaving: false,
      externalEdit: null,
      externalEditPhone: '',
      externalEditName: '',
      externalEditOptIn: false,
      externalEditError: '',
      externalEditReady: false,
      externalEditSaving: false,
      optInPopup: '',
      highlightId: null,
      showOptInGateEditor: false,
      optInGateDraft: '',
      optInGateError: '',
      optInGateSaving: false,
      composeOptInOnce: '',
      showSchedule: false,
      scheduleLocal: '',
      scheduleError: '',
      suppressSendClick: false,
      sendHoldTimer: null,
      sendHoldStart: null,
      retryMessage: null,
      retrying: false,
      deleting: false,
      collapsed: {
        today: dateGroupCollapsedByDefault('today'),
        yesterday: dateGroupCollapsedByDefault('yesterday'),
        week: dateGroupCollapsedByDefault('week'),
        older: dateGroupCollapsedByDefault('older'),
      },
    };
  },
  computed: {
    isEmbedded() {
      return smsInboxIsEmbedded(this.$route && this.$route.path);
    },
    visibleInbox() {
      const searching = this.searchAll && this.listQuery.trim();
      const source = searching ? this.searchInbox : this.inbox;
      return filterSmsInboxSearch(
        filterSmsInboxByParty(source, this.partyFilter),
        this.listQuery,
      );
    },
    groupedInbox() {
      void this.clock;
      return groupSmsInboxByDate(this.visibleInbox, new Date(this.clock));
    },
    newNumberOption() {
      return smsNewNumberOption(this.searchQuery, this.searchHits);
    },
    recipientSearchStatus() {
      return smsRecipientSearchStatus({
        query: this.searchQuery,
        searching: this.searching,
        hits: this.searchHits,
        failed: this.searchFailed,
        hasNewNumber: !!this.newNumberOption,
      });
    },
    unknownContactPrompt() {
      const contact = this.unknownContact;
      if (!contact) return '';
      const phone = String(contact.prefix || '')
        .replace(/\s*·\s*$/, '')
        .trim();
      const shown = phone || contact.phoneDigits;
      return `Name ${shown}. Saving opts this number in.`;
    },
    externalEditCanSave() {
      return (
        !!this.externalEdit &&
        this.externalEditReady &&
        !this.externalEditSaving &&
        !!String(this.externalEditName || '').trim() &&
        !!parseSmsSearchPhone(this.externalEditPhone)
      );
    },
    scheduleMin() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`
      );
    },
  },
  watch: {
    inbox: 'tryApplyPushHighlight',
    '$route.query.id': 'tryApplyPushHighlight',
    listQuery(value) {
      if (this.searchAll && String(value || '').trim()) this.fetchInboxSearch();
      else if (this.searchAll) this.clearInboxSearch();
    },
  },
  mounted() {
    this.markStaffSmsSeen();
    const cached = keepRecentSmsInbox(readSmsInboxCache(window.localStorage));
    if (cached.length) {
      this.inbox = cached;
      this.refreshSmsMedia(this.inbox);
      this.scrollInboxToLatest();
      this.fetchInbox({ silent: true });
    } else {
      this.fetchInbox();
    }
    trimCachedSmsPhotos();
    this.connectSocket();
    this.clockTimer = setInterval(() => {
      this.clock = Date.now();
    }, 30000);
    window.addEventListener('keydown', this.onPanelKeydown);
    eventBus.on('smsComposeTo', this.applyExternalCompose);
    eventBus.on('smsInboxScrollLatest', this.onSmsInboxScrollLatest);
    document.addEventListener('visibilitychange', this.markStaffSmsSeen);
    this.applyRouteCompose();
    this.scheduleSmsRefresh(0);
  },
  beforeUnmount() {
    this._smsRefreshStopped = true;
    this.clearSmsRefresh();
    this._smsMediaGen = (this._smsMediaGen || 0) + 1;
    revokeSmsMediaUrls(this.smsMediaUrls);
    if (this._highlightTimer) clearTimeout(this._highlightTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.listFilterTimer) clearTimeout(this.listFilterTimer);
    this.clearLongPress();
    this.clearSendHold();
    window.removeEventListener('keydown', this.onPanelKeydown);
    this.disconnectSocket();
    eventBus.off('smsComposeTo', this.applyExternalCompose);
    eventBus.off('smsInboxScrollLatest', this.onSmsInboxScrollLatest);
    document.removeEventListener('visibilitychange', this.markStaffSmsSeen);
  },
  methods: {
    async markStaffSmsSeen() {
      if (!smsTabIsVisible(document)) return;
      try {
        await axios.post('/api/staff-sms/seen');
      } catch (err) {
        console.error('Failed to mark staff SMS seen', err);
      }
    },
    sendClose() {
      eventBus.emit('requestCloseSmsInbox');
    },
    toggleGroup(key) {
      this.collapsed[key] = !this.collapsed[key];
    },
    stamp(date, expanded) {
      void this.clock;
      return formatSmsInboxStamp(date, expanded);
    },
    replyPreview(body) {
      return inReplyToPreview(body, 72);
    },
    smsInboxIsReply,
    unknownSmsContactTarget,
    externalSmsContactTarget,
    persistInbox(rows) {
      this.inbox = keepRecentSmsInbox(rows);
      writeSmsInboxCache(this.inbox, window.localStorage);
      if (!(this.searchAll && this.listQuery.trim())) {
        this.refreshSmsMedia(this.inbox);
      }
    },
    async refreshSmsMedia(rows) {
      const generation = (this._smsMediaGen = (this._smsMediaGen || 0) + 1);
      const createdAtByMedia = smsMediaMessageTimes(rows);
      const { urls, revoked } = await loadSmsMediaObjectUrls(
        rows,
        this.smsMediaUrls,
        {
          fetchBlob: async (id) => {
            const cached = await getCachedSmsPhoto(id);
            if (cached) return cached;
            const blob = await axios
              .get(`/api/sms/media/${id}`, { responseType: 'blob' })
              .then((res) => res.data);
            const messageCreatedAt = createdAtByMedia.get(Number(id));
            if (blob && smsPhotoIsWithinDays(messageCreatedAt)) {
              await putCachedSmsPhoto({
                id,
                blob,
                messageCreatedAt,
                contentType: blob.type,
              });
            }
            return blob;
          },
          createObjectURL: (blob) => URL.createObjectURL(blob),
        },
      );
      trimCachedSmsPhotos();
      if (this._smsMediaGen !== generation) {
        const live = new Set(Object.values(this.smsMediaUrls || {}));
        revokeSmsMediaUrls(
          Object.fromEntries(
            Object.entries(urls).filter(([, url]) => !live.has(url)),
          ),
        );
        return;
      }
      revokeSmsMediaUrls(Object.fromEntries(revoked.map((url) => [url, url])));
      this.smsMediaUrls = urls;
    },
    inboxPinnedToLatest() {
      const el = this.$refs.smsList;
      if (!el) return true;
      return el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    },
    scrollInboxToLatest(opts) {
      const force = !!(opts && opts.force);
      if (!force && this.$route && this.$route.query && this.$route.query.id) return;
      this.$nextTick(() => {
        const el = this.$refs.smsList;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
      });
    },
    tryApplyPushHighlight() {
      const plan = pushHighlightPlan({
        query: this.$route && this.$route.query,
        inbox: this.inbox,
        now: new Date(this.clock),
      });
      if (!plan || !plan.found) return;
      if (this._appliedPushHighlightId === plan.id) return;
      this._appliedPushHighlightId = plan.id;
      this.clearListFilter();
      this.clearPartyFilter();
      if (plan.expandGroup) this.collapsed[plan.expandGroup] = false;
      this.selectedId = plan.id;
      this.highlightId = plan.id;
      this.$nextTick(() => {
        const el =
          this.$el &&
          this.$el.querySelector(`[data-push-id="${plan.id}"]`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
      if (this._highlightTimer) clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        if (this.highlightId === plan.id) this.highlightId = null;
      }, 3600);
    },
    onSmsInboxScrollLatest() {
      this._forceScrollLatest = true;
      this.scrollInboxToLatest({ force: true });
      if (!this._inboxLoads) this._forceScrollLatest = false;
    },
    async fetchInbox({ silent } = {}) {
      this._inboxLoads = (this._inboxLoads || 0) + 1;
      const hasRows = this.inbox.length > 0;
      const stick = !silent || this.inboxPinnedToLatest();
      if (!silent && !hasRows) {
        this.loading = true;
        this.loadError = '';
      }
      if (!silent) this.refreshing = true;
      try {
        const { data } = await axios.get('/api/sms/inbox');
        this.persistInbox(Array.isArray(data) ? data : []);
        this.loadError = '';
      } catch (err) {
        console.error(err);
        if (!silent && !hasRows) {
          this.loadError = 'Failed to load incoming texts.';
        }
      } finally {
        this._inboxLoads = Math.max(0, (this._inboxLoads || 1) - 1);
        this.loading = false;
        if (!silent) this.refreshing = false;
        if (this._forceScrollLatest) {
          this._forceScrollLatest = false;
          this.scrollInboxToLatest({ force: true });
        } else if (stick) {
          this.scrollInboxToLatest();
        }
      }
    },
    refreshInbox() {
      const pending = this.fetchInbox({ silent: true });
      if (this.searchAll && this.listQuery.trim()) {
        return Promise.all([pending, this.fetchInboxSearch()]);
      }
      return pending;
    },
    manualRefresh() {
      this.noteSmsActivity();
      const pending = this.fetchInbox({ silent: false });
      if (this.searchAll && this.listQuery.trim()) {
        return Promise.all([pending, this.fetchInboxSearch()]);
      }
      return pending;
    },
    clearSmsRefresh() {
      if (this._smsRefreshTimer) clearTimeout(this._smsRefreshTimer);
      this._smsRefreshTimer = null;
    },
    scheduleSmsRefresh(step) {
      this.clearSmsRefresh();
      this._smsRefreshGen = (this._smsRefreshGen || 0) + 1;
      const gen = this._smsRefreshGen;
      this._smsRefreshStep = step;
      this._smsRefreshTimer = setTimeout(() => {
        if (gen !== this._smsRefreshGen) return;
        this.pollSmsInbox(gen);
      }, smsRefreshDelay(step));
    },
    async pollSmsInbox(gen) {
      if (this._smsRefreshStopped) return;
      if (this._smsPolling) {
        this.scheduleSmsRefresh(this._smsRefreshStep || 0);
        return;
      }
      this._smsPolling = true;
      const before = smsInboxActivityKey(this.inbox);
      try {
        await this.refreshInbox();
      } finally {
        this._smsPolling = false;
        if (gen !== this._smsRefreshGen || this._smsRefreshStopped) return;
        const step = nextSmsRefreshStep(
          this._smsRefreshStep,
          before,
          smsInboxActivityKey(this.inbox),
          this.inbox,
        );
        this.scheduleSmsRefresh(step);
      }
    },
    noteSmsActivity() {
      this._smsRefreshStep = 0;
      this.scheduleSmsRefresh(0);
    },
    toggleSearchAll() {
      this.searchAll = !this.searchAll;
      if (this.searchAll && this.listQuery.trim()) this.fetchInboxSearch();
      else this.clearInboxSearch();
    },
    clearInboxSearch() {
      this._searchGen = (this._searchGen || 0) + 1;
      this.searchInbox = [];
      this.searchingAll = false;
      this.searchError = '';
      this.refreshSmsMedia(this.inbox);
    },
    async fetchInboxSearch() {
      const q = this.listQuery.trim();
      if (!this.searchAll || !q) return;
      const generation = (this._searchGen = (this._searchGen || 0) + 1);
      this.searchingAll = true;
      this.searchError = '';
      try {
        const { data } = await axios.get('/api/sms/inbox', { params: { q } });
        if (this._searchGen !== generation) return;
        this.searchInbox = Array.isArray(data) ? data : [];
        this.refreshSmsMedia(this.searchInbox);
      } catch (err) {
        console.error(err);
        if (this._searchGen !== generation) return;
        this.searchInbox = [];
        this.searchError = 'Could not search older messages.';
      } finally {
        if (this._searchGen === generation) this.searchingAll = false;
      }
    },
    handleSmsInboxInbound(payload) {
      const stick = this.inboxPinnedToLatest();
      this.persistInbox(mergeSmsInboxMessages(this.inbox, payload));
      if (stick) this.scrollInboxToLatest();
      this.noteSmsActivity();
    },
    onListFilterInput() {
      if (this.listFilterTimer) clearTimeout(this.listFilterTimer);
      this.listFilterTimer = setTimeout(() => {
        this.listQuery = this.listFilterInput;
      }, 220);
    },
    clearListFilter() {
      if (this.listFilterTimer) clearTimeout(this.listFilterTimer);
      this.listFilterInput = '';
      this.listQuery = '';
    },
    onComposeKeydown(event) {
      if (!smsComposeCtrlEnter(event)) return;
      event.preventDefault();
      this.sendReply();
    },
    onPanelKeydown(event) {
      const hotkey = smsInboxHotkey(event, {
        pointerInside: this.smsPointerInside,
        blocked: !!(
          this.unknownContact ||
          this.externalEdit ||
          this.showOptInGateEditor ||
          this.showSchedule
        ),
      });
      if (hotkey === 'new') {
        event.preventDefault();
        this.scrollInboxToLatest({ force: true });
        this.$nextTick(() => this.openSearch());
        return;
      }
      if (hotkey === 'reply') {
        event.preventDefault();
        this.replyToLatestSender();
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.showSchedule) {
        event.preventDefault();
        this.closeSchedule();
        return;
      }
      if (this.showOptInGateEditor || this.unknownContact || this.externalEdit) return;
      const action = smsInboxEscapeAction({
        showSearch: this.showSearch,
        hasCompose: !!this.composeTarget,
        hasListFilter: !!(this.listFilterInput || this.listQuery),
        hasPartyFilter: !!this.partyFilter,
      });
      if (!action) return;
      event.preventDefault();
      if (action === 'closeSearch') {
        this.closeSearch();
        return;
      }
      if (action === 'closeCompose') {
        this.closeCompose();
        return;
      }
      this.clearListFilter();
      this.clearPartyFilter();
    },
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;
        if (!socketUrl) return;
        const token = localStorage.getItem('token');
        this.disconnectSocket();
        this.socket = io(socketUrl, {
          query: { token, source: 'mSmsInbox' },
          transports: ['websocket', 'polling'],
          reconnection: true,
        });
        this.socket.on('connect', () => {
          if (token) this.socket.emit('authenticate', token);
        });
        this.socket.on('smsInboxInbound', this.handleSmsInboxInbound);
        this.socket.on('staffSmsInbound', this.handleStaffSmsInbound);
      } catch (err) {
        console.error('MSmsInbox socket connect failed:', err);
      }
    },
    handleStaffSmsInbound() {
      this.noteSmsActivity();
      this.fetchInbox({ silent: true });
    },
    disconnectSocket() {
      if (!this.socket) return;
      this.socket.off('smsInboxInbound', this.handleSmsInboxInbound);
      this.socket.off('staffSmsInbound', this.handleStaffSmsInbound);
      this.socket.disconnect();
      this.socket = null;
    },
    sendDot(msg) {
      return smsInboxSendDot(msg);
    },
    scheduledNotice(msg) {
      if (!msg || !msg.sendScheduled) return '';
      return scheduledSmsNotice(msg.scheduledFor);
    },
    clearSendHold() {
      if (this.sendHoldTimer) {
        clearTimeout(this.sendHoldTimer);
        this.sendHoldTimer = null;
      }
      this.sendHoldStart = null;
    },
    onSendPointerDown(event) {
      if (this.sending || this.composePhotoBusy) return;
      if (!this.replyBody.trim() && !this.composePhoto) return;
      if (event && event.pointerType === 'mouse' && event.button !== 0) return;
      this.clearSendHold();
      const point = event
        ? { x: event.clientX, y: event.clientY, id: event.pointerId }
        : null;
      this.sendHoldStart = point;
      if (
        point &&
        event.currentTarget &&
        event.currentTarget.setPointerCapture
      ) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (err) {
          /* the hold still works without capture */
        }
      }
      this.sendHoldTimer = setTimeout(() => {
        this.sendHoldTimer = null;
        this.suppressSendClick = true;
        this.openSchedule();
      }, SMS_SEND_HOLD_MS);
    },
    onSendPointerMove(event) {
      if (!this.sendHoldTimer || !this.sendHoldStart || !event) return;
      const dx = event.clientX - this.sendHoldStart.x;
      const dy = event.clientY - this.sendHoldStart.y;
      if (Math.hypot(dx, dy) > SMS_SEND_HOLD_MOVE_PX) this.clearSendHold();
    },
    onSendPointerEnd() {
      const opened = this.suppressSendClick;
      this.clearSendHold();
      if (!opened) return;
      setTimeout(() => {
        this.suppressSendClick = false;
      }, 400);
    },
    onSendClick() {
      if (this.suppressSendClick) {
        this.suppressSendClick = false;
        return;
      }
      this.sendReply();
    },
    openSchedule() {
      if (this.sending || this.composePhotoBusy) return;
      if (!this.replyBody.trim() && !this.composePhoto) return;
      this.scheduleError = '';
      this.scheduleLocal = defaultScheduleLocalValue(new Date());
      this.showSchedule = true;
    },
    closeSchedule() {
      this.showSchedule = false;
      this.scheduleError = '';
    },
    confirmSchedule() {
      const iso = sendAtFromLocalInput(this.scheduleLocal);
      if (!iso) {
        this.scheduleError = 'Pick a later date and time.';
        return;
      }
      this.closeSchedule();
      this.sendReply(iso);
    },
    restoreFailedToInput(msg) {
      const restored = composeRestoreFromFailed(msg);
      if (!restored) return;
      this.composeOptInOnce = '';
      this.composeTarget = restored.composeTarget;
      this.replyBody = restored.body;
      this.sendError = '';
      this.focusComposeInput();
    },
    focusComposeInput() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector('.sms-compose-input');
        if (el && el.focus) el.focus();
      });
    },
    focusReplyAfterSearch() {
      if (!String(this.listQuery || '').trim()) return;
      this.focusComposeInput();
    },
    applyRouteCompose() {
      const phoneDigits = parseSmsSearchPhone(
        this.$route && this.$route.query && this.$route.query.composePhone,
      );
      if (!phoneDigits) return;
      const name = String(
        (this.$route.query && this.$route.query.composeName) || '',
      ).trim();
      this.applyExternalCompose({ phoneDigits, name });
    },
    applyExternalCompose(target) {
      const phoneDigits = parseSmsSearchPhone(target && target.phoneDigits);
      if (!phoneDigits) return;
      const name = String((target && target.name) || '').trim();
      this.showSearch = false;
      this.selectedId = null;
      this.expandedId = null;
      this.replyBody = '';
      this.sendError = '';
      this.composeOptInOnce = '';
      this.composeTarget = {
        peopleId: null,
        userId: null,
        phoneDigits,
        externalName: name,
        label: smsInboxHeader({
          kind: 'external',
          name,
          phoneDigits,
        }),
        inReplyToId: null,
      };
      this.focusComposeInput();
    },
    onItemPointerDown(msg, event) {
      if (!msg || !msg.sendFailed) return;
      if (event && event.pointerType === 'mouse' && event.button !== 0) return;
      if (
        event &&
        event.target &&
        event.target.closest &&
        event.target.closest('.sms-body, .sms-in-reply')
      ) {
        return;
      }
      this.clearLongPress();
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        this.skipNextSelect = true;
        this.restoreFailedToInput(msg);
      }, 500);
    },
    onItemPointerUp() {
      this.clearLongPress();
    },
    clearLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    selectMessage(msg, event) {
      if (this.skipNextSelect) {
        this.skipNextSelect = false;
        return;
      }
      if (smsSelectionIsText(window.getSelection && window.getSelection())) {
        return;
      }
      const body =
        event && event.currentTarget
          ? event.currentTarget.querySelector('.sms-body')
          : null;
      const action = smsSelectAction({
        clipped: smsBodyIsClipped(body),
        alreadyExpanded: this.expandedId === msg.id,
      });
      if (action === 'expand') {
        this.expandedId = msg.id;
        this.selectedId = msg.id;
        this.retryMessage = null;
        this.composeTarget = null;
        this.replyBody = '';
        this.composeOptInOnce = '';
        this.sendError = '';
        return;
      }
      this.expandedId = msg.id;
      this.selectedId = msg.id;
      if (smsCanRetryPending(msg)) {
        this.composeTarget = null;
        this.retryMessage = msg;
        return;
      }
      this.retryMessage = null;
      if (this.composeTarget && this.composeTarget.inReplyToId === msg.id) {
        this.focusReplyAfterSearch();
        return;
      }
      this.sendError = '';
      this.replyBody = '';
      this.composeOptInOnce = '';
      if (msg.peopleId || msg.userId || msg.phoneDigits) {
        this.composeTarget = {
          peopleId: msg.peopleId,
          userId: msg.userId,
          phoneDigits: msg.phoneDigits,
          externalName: msg.kind === 'external' ? msg.name : '',
          label: msg.header,
          inReplyToId: msg.id,
        };
        this.focusReplyAfterSearch();
      } else {
        this.composeTarget = null;
        this.sendError = 'Unknown sender — cannot reply.';
      }
    },
    onListClick() {
      /* keep selection; tapping empty list area does not clear */
    },
    filterByParty(msg) {
      const next = nextSmsInboxPartyFilter(this.partyFilter, msg);
      this.partyFilter = next;
      this.partyFilterLabel = next ? msg.header : '';
    },
    openUnknownContact(msg) {
      const target = unknownSmsContactTarget(msg);
      if (!target) return;
      this.unknownContact = target;
      this.unknownContactName = '';
      this.unknownContactError = '';
      this.$nextTick(() => {
        const el = this.$refs.unknownContactInput;
        if (el && el.focus) el.focus();
      });
    },
    closeUnknownContact() {
      if (this.unknownContactSaving) return;
      this.unknownContact = null;
      this.unknownContactName = '';
      this.unknownContactError = '';
    },
    async saveUnknownContact() {
      const contact = this.unknownContact;
      const name = String(this.unknownContactName || '').trim();
      if (!contact || !name || this.unknownContactSaving) return;
      this.unknownContactSaving = true;
      this.unknownContactError = '';
      try {
        await axios.post('/api/sms/external-contact', {
          phoneDigits: contact.phoneDigits,
          name,
          optIn: true,
        });
        const header = smsInboxHeader({
          kind: 'external',
          name,
          phoneDigits: contact.phoneDigits,
        });
        this.persistInbox(
          applyExternalContactToInbox(this.inbox, {
            phoneDigits: contact.phoneDigits,
            name,
          }),
        );
        if (this.partyFilter === `phone:${contact.phoneDigits}`) {
          this.partyFilterLabel = header;
        }
        if (
          this.composeTarget &&
          parseSmsSearchPhone(this.composeTarget.phoneDigits) ===
            contact.phoneDigits
        ) {
          this.composeTarget = {
            ...this.composeTarget,
            externalName: name,
            label: header,
          };
        }
        this.unknownContactSaving = false;
        this.closeUnknownContact();
      } catch (err) {
        this.unknownContactError =
          (err.response && err.response.data && err.response.data.error) ||
          'Failed to save contact';
      } finally {
        this.unknownContactSaving = false;
      }
    },
    openExternalEdit(msg) {
      const target = externalSmsContactTarget(msg);
      if (!target) return;
      if (!this.unknownContactSaving) this.closeUnknownContact();
      this.externalEdit = { fromPhoneDigits: target.phoneDigits };
      this.externalEditPhone = formatSmsPhone(target.phoneDigits);
      this.externalEditName = target.name;
      this.externalEditOptIn = false;
      this.externalEditError = '';
      this.externalEditReady = false;
      this.externalEditSaving = false;
      this.loadExternalEdit(target.phoneDigits);
    },
    focusExternalEdit() {
      const el = this.$refs.externalEditPhoneInput;
      if (el && el.focus) el.focus();
    },
    async loadExternalEdit(phoneDigits) {
      const loadId = (this._externalEditLoad || 0) + 1;
      this._externalEditLoad = loadId;
      try {
        const { data } = await axios.get('/api/sms/external-contact', {
          params: { phoneDigits },
        });
        if (this._externalEditLoad !== loadId || !this.externalEdit) return;
        this.externalEditPhone =
          formatSmsPhone(data && data.phoneDigits) || this.externalEditPhone;
        this.externalEditName =
          String((data && data.name) || '').trim() || this.externalEditName;
        this.externalEditOptIn = !!(data && data.optedIn);
        this.externalEditReady = true;
      } catch (err) {
        if (this._externalEditLoad !== loadId || !this.externalEdit) return;
        const reason =
          err.response && err.response.data && err.response.data.error;
        this.externalEditError = externalContactEditError(reason);
      } finally {
        if (this._externalEditLoad === loadId && this.externalEdit) {
          this.$nextTick(() => this.focusExternalEdit());
        }
      }
    },
    closeExternalEdit() {
      if (this.externalEditSaving) return;
      this._externalEditLoad = (this._externalEditLoad || 0) + 1;
      this.externalEdit = null;
      this.externalEditPhone = '';
      this.externalEditName = '';
      this.externalEditOptIn = false;
      this.externalEditError = '';
      this.externalEditReady = false;
    },
    async saveExternalEdit() {
      if (!this.externalEditCanSave) return;
      const fromPhoneDigits = this.externalEdit.fromPhoneDigits;
      const phoneDigits = this.externalEditPhone;
      const name = String(this.externalEditName || '').trim();
      const optIn = !!this.externalEditOptIn;
      this.externalEditSaving = true;
      this.externalEditError = '';
      try {
        const { data } = await axios.put('/api/sms/external-contact', {
          fromPhoneDigits,
          phoneDigits,
          name,
          optIn,
          userAgent: 'SMS Messaging',
        });
        const savedFrom = (data && data.fromPhoneDigits) || fromPhoneDigits;
        const savedDigits = (data && data.phoneDigits) || parseSmsSearchPhone(phoneDigits);
        const savedName = (data && data.name) || name;
        const header = smsInboxHeader({
          kind: 'external',
          name: savedName,
          phoneDigits: savedDigits,
        });
        this.persistInbox(
          applyExternalContactEditToInbox(this.inbox, {
            fromPhoneDigits: savedFrom,
            phoneDigits: savedDigits,
            name: savedName,
          }),
        );
        if (this.partyFilter === `phone:${savedFrom}`) {
          this.partyFilter = `phone:${savedDigits}`;
          this.partyFilterLabel = header;
        }
        if (
          this.composeTarget &&
          parseSmsSearchPhone(this.composeTarget.phoneDigits) === savedFrom
        ) {
          this.composeTarget = {
            ...this.composeTarget,
            phoneDigits: savedDigits,
            externalName: savedName,
            label: header,
          };
        }
        this.externalEditSaving = false;
        this.closeExternalEdit();
      } catch (err) {
        const reason =
          err.response && err.response.data && err.response.data.error;
        this.externalEditError = externalContactEditError(reason);
      } finally {
        this.externalEditSaving = false;
      }
    },
    clearComposePhoto() {
      if (this.composePhoto && this.composePhoto.url && typeof URL !== 'undefined') {
        URL.revokeObjectURL(this.composePhoto.url);
      }
      this.composePhoto = null;
    },
    async onComposePhoto(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0];
      if (input) input.value = '';
      if (!file || this.sending || this.composePhotoBusy) return;
      this.composePhotoBusy = true;
      this.sendError = '';
      try {
        const blob = await smsPhotoToJpeg(file);
        const url = URL.createObjectURL(blob);
        this.clearComposePhoto();
        this.composePhoto = { blob, url };
      } catch (err) {
        this.sendError = 'That photo could not be prepared. Choose a JPEG or PNG.';
      } finally {
        this.composePhotoBusy = false;
      }
    },
    closeCompose() {
      this.clearComposePhoto();
      this.composeTarget = null;
      this.replyBody = '';
      this.composeOptInOnce = '';
      this.sendError = '';
      this.showOptInGateEditor = false;
      this.selectedId = null;
    },
    closeOptInGateEditor() {
      this.showOptInGateEditor = false;
      this.optInGateError = '';
    },
    useBasicOptInGate() {
      this.composeOptInOnce = BASIC_OPT_IN_GATE_BODY;
      this.showOptInGateEditor = false;
      this.optInGateError = '';
      this._optInGateLoad = (this._optInGateLoad || 0) + 1;
    },
    async openOptInGateEditor() {
      const loadId = (this._optInGateLoad || 0) + 1;
      this._optInGateLoad = loadId;
      this.optInGateDraft = DEFAULT_OPT_IN_GATE_BODY;
      this.showOptInGateEditor = true;
      this.optInGateError = '';
      try {
        const { data } = await axios.get('/api/sms/opt-in-gate');
        if (loadId !== this._optInGateLoad || !this.showOptInGateEditor) return;
        this.optInGateDraft = optInGateEditorText(data);
      } catch (err) {
        if (loadId !== this._optInGateLoad || !this.showOptInGateEditor) return;
        this.optInGateDraft = DEFAULT_OPT_IN_GATE_BODY;
        this.optInGateError =
          err.response?.data?.error || 'Failed to load opt-in message';
      }
    },
    async saveOptInGate() {
      const body = String(this.optInGateDraft || '').trim();
      if (!body || this.optInGateSaving) return;
      this.optInGateSaving = true;
      this.optInGateError = '';
      try {
        const { data } = await axios.put('/api/sms/opt-in-gate', { body });
        this.optInGateDraft = (data && data.body) || body;
        this.showOptInGateEditor = false;
        this._optInGateLoad = (this._optInGateLoad || 0) + 1;
      } catch (err) {
        this.optInGateError =
          err.response?.data?.error || 'Failed to save opt-in message';
      } finally {
        this.optInGateSaving = false;
      }
    },
    clearPartyFilter() {
      this.partyFilter = null;
      this.partyFilterLabel = '';
    },
    replyToLatestSender() {
      this.closeSearch();
      const msg = smsInboxLastSender(this.visibleInbox);
      if (!msg) return;
      const bucket = dateBucket(
        msg.createdAt || msg.createdOn,
        new Date(this.clock),
      );
      if (bucket) this.collapsed[bucket] = false;
      this.scrollInboxToLatest({ force: true });
      this.$nextTick(() => {
        this.selectMessage(msg);
        this.focusComposeInput();
      });
    },
    openSearch() {
      const fromFilter = composeTargetFromPartyFilter(
        this.inbox,
        this.partyFilter,
        this.partyFilterLabel,
      );
      if (fromFilter) {
        this.showSearch = false;
        this.selectedId = null;
        this.expandedId = null;
        this.replyBody = '';
        this.sendError = '';
        this.composeOptInOnce = '';
        this.composeTarget = fromFilter;
        this.focusComposeInput();
        return;
      }
      this.showSearch = true;
      this.searchQuery = '';
      this.searchHits = [];
      this.searchFailed = false;
      this.externalNameInput = '';
      this.$nextTick(() => {
        const el = this.$refs.searchInput;
        if (el && el.focus) el.focus();
      });
    },
    closeSearch() {
      this.showSearch = false;
      this.searchHits = [];
      this.searchQuery = '';
      this.searchFailed = false;
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.runSearch(), 220);
    },
    async runSearch() {
      const q = this.searchQuery.trim();
      const searchId = (this._searchId || 0) + 1;
      this._searchId = searchId;
      if (!q) {
        this.searchHits = [];
        this.searchFailed = false;
        this.searching = false;
        return;
      }
      this.searching = true;
      this.searchFailed = false;
      try {
        const { data } = await axios.get('/api/sms/recipients', {
          params: { q },
        });
        if (searchId !== this._searchId) return;
        this.searchHits = Array.isArray(data) ? data : [];
      } catch (err) {
        if (searchId !== this._searchId) return;
        console.error(err);
        this.searchHits = [];
        this.searchFailed = true;
      } finally {
        if (searchId === this._searchId) this.searching = false;
      }
    },
    pickRecipient(hit) {
      this.closeSearch();
      this.selectedId = null;
      this.expandedId = null;
      this.replyBody = '';
      this.sendError = '';
      this.composeOptInOnce = '';
      if (hit.kind === 'external') {
        const phoneDigits =
          parseSmsSearchPhone(hit.phoneDigits || hit.phone) || '';
        this.composeTarget = {
          peopleId: null,
          userId: null,
          phoneDigits,
          externalName: String(hit.name || '').trim(),
          label: smsInboxHeader({
            kind: 'external',
            name: hit.name,
            phoneDigits,
          }),
          inReplyToId: null,
        };
      } else {
        const bits = [hit.name, hit.property].filter(Boolean);
        this.composeTarget = {
          peopleId: hit.peopleId,
          userId: hit.userId,
          phoneDigits: hit.phone || '',
          externalName: '',
          label:
            hit.kind === 'staff' ? `${hit.name} (staff)` : bits.join(' · '),
          inReplyToId: null,
        };
      }
      this.focusComposeInput();
    },
    pickNewNumber() {
      const opt = this.newNumberOption;
      if (!opt) return;
      const externalName = this.externalNameInput.trim();
      this.closeSearch();
      this.selectedId = null;
      this.expandedId = null;
      this.replyBody = '';
      this.sendError = '';
      this.externalNameInput = '';
      this.composeOptInOnce = '';
      this.composeTarget = {
        peopleId: null,
        userId: null,
        phoneDigits: opt.phoneDigits,
        externalName,
        label: smsInboxHeader({
          kind: 'external',
          name: externalName,
          phoneDigits: opt.phoneDigits,
        }),
        inReplyToId: null,
      };
      this.focusComposeInput();
    },
    applySendResult(message) {
      if (!message || message.id == null) return;
      this.persistInbox(
        mergeSmsInboxMessages(this.inbox, { messages: [message] }),
      );
      this.scrollInboxToLatest();
      this.noteSmsActivity();
    },
    async deleteSelected() {
      const msg = this.retryMessage;
      if (!msg || !msg.sendFailed || this.deleting) return;
      const id = msg.id;
      this.deleting = true;
      this.sendError = '';
      try {
        await axios.delete(`/api/sms/messages/${id}`);
        this.persistInbox(dropInboxMessageById(this.inbox, id));
        this.searchInbox = dropInboxMessageById(this.searchInbox, id);
        if (Number(this.selectedId) === Number(id)) this.selectedId = null;
        if (Number(this.expandedId) === Number(id)) this.expandedId = null;
        this.retryMessage = null;
        this.noteSmsActivity();
      } catch (err) {
        this.sendError = err.response?.data?.error || 'Failed to delete';
      } finally {
        this.deleting = false;
      }
    },
    async retrySelected() {
      const msg = this.retryMessage;
      if (!msg || this.retrying) return;
      this.retrying = true;
      this.sendError = '';
      try {
        const { data } = await axios.post('/api/sms/send', { retryId: msg.id });
        if (data && data.message) this.applySendResult(data.message);
        this.retryMessage = null;
      } catch (err) {
        if (err.response?.data?.reason === 'pending_opt_in') {
          this.optInPopup =
            err.response.data.error ||
            'This number has not opted in yet. Wait for YES or START.';
        } else {
          this.sendError = err.response?.data?.error || 'Failed to retry';
        }
      } finally {
        this.retrying = false;
      }
    },
    async sendReply(sendAt) {
      if (!this.composeTarget || this.sending || this.composePhotoBusy) return;
      const body = this.replyBody.trim();
      const photo = this.composePhoto;
      const when = typeof sendAt === 'string' ? sendAt : '';
      if (!body && !photo) return;
      const pendingWarn = smsPendingOptInPopup({
        peopleId: this.composeTarget.peopleId,
        userId: this.composeTarget.userId,
        inbox: this.inbox,
      });
      if (pendingWarn) {
        this.optInPopup = pendingWarn;
        return;
      }
      let photoData = '';
      if (photo && photo.blob) {
        try {
          photoData = await blobToJpegDataUrl(photo.blob);
        } catch (err) {
          this.sendError = 'That photo could not be prepared. Choose a JPEG or PNG.';
          return;
        }
      }
      const payload = {
        peopleId: this.composeTarget.peopleId,
        userId: this.composeTarget.userId,
        phoneDigits: this.composeTarget.phoneDigits,
        externalName: this.composeTarget.externalName,
        body,
        inReplyToId: this.composeTarget.inReplyToId,
        optInGateOnce: this.composeOptInOnce || undefined,
        photo: photoData || undefined,
      };
      if (when) payload.sendAt = when;
      const optimistic = optimisticOutboundMessage({
        body,
        composeTarget: this.composeTarget,
        scheduledFor: when || null,
        media: photo && photo.url
          ? [{ previewUrl: photo.url, contentType: 'image/jpeg' }]
          : [],
      });
      this.replyBody = '';
      this.composePhoto = null;
      this.sendError = '';
      this.sending = true;
      this.applySendResult(optimistic);
      try {
        const { data } = await axios.post('/api/sms/send', payload);
        this.persistInbox(
          mergeSmsInboxMessages(
            dropInboxMessageById(this.inbox, optimistic.id),
            { messages: [data && data.message].filter(Boolean) },
          ),
        );
        this.scrollInboxToLatest();
        this.noteSmsActivity();
        this.composeTarget = null;
        this.replyBody = '';
        this.composeOptInOnce = '';
        this.showOptInGateEditor = false;
        if (photo && photo.url && typeof URL !== 'undefined') {
          URL.revokeObjectURL(photo.url);
        }
      } catch (err) {
        this.persistInbox(dropInboxMessageById(this.inbox, optimistic.id));
        this.replyBody = body;
        if (photo) this.composePhoto = photo;
        if (err.response?.data?.reason === 'pending_opt_in') {
          this.optInPopup =
            err.response.data.error ||
            smsPendingOptInPopup({
              peopleId: this.composeTarget.peopleId,
              userId: this.composeTarget.userId,
              inbox: [{ peopleId: this.composeTarget.peopleId, pendingOptIn: true }],
            });
        } else {
          this.sendError = err.response?.data?.error || 'Failed to send';
        }
      } finally {
        this.sending = false;
      }
    },

  },
};
</script>

<style scoped>
.sms-page {
  width: 100%;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  position: relative;
  font-family: Arial, 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji',
    sans-serif;
}

.title-bar {
  flex-shrink: 0;
  background: #6e5875;
  color: #fff;
  font-size: 1.15em;
  font-weight: 600;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.title-buttons {
  display: flex;
  gap: 6px;
}

.title-button {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.title-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.sms-hint {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 12px 6px;
  font-size: 0.75em;
  color: #8a7a90;
}

.sms-search-all {
  flex-shrink: 0;
  border: 1px solid #c4b4c8;
  background: transparent;
  color: #6e5875;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 1em;
  line-height: 1.5;
  cursor: pointer;
}

.sms-search-all-on {
  background: #ede4f0;
  border-color: #6e5875;
}

.sms-list-filter {
  flex-shrink: 0;
  padding: 0 12px 8px;
}

.sms-list-filter-wrap {
  position: relative;
  max-width: 88%;
}

.sms-list-filter-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d4c8d8;
  border-radius: 6px;
  padding: 8px 32px 8px 10px;
  font-size: 0.92em;
}

.sms-list-filter-clear {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #6a5570;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.sms-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 12px 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sms-list::-webkit-scrollbar {
  display: none;
}

.sms-group {
  margin-top: 14px;
  margin-bottom: 10px;
}

.sms-group:first-child {
  margin-top: 6px;
}

.sms-group-older {
  margin-top: 28px;
}

.sms-group-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e0d6e4;
  background: #f3eef6;
  color: #4a3a52;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;
  text-align: left;
}

.sms-group-header i {
  width: 12px;
  font-size: 0.75em;
  color: #6a5570;
}

.sms-group-count {
  margin-left: auto;
  font-weight: 500;
  color: #8a7a90;
  font-size: 0.9em;
}

.sms-status {
  padding: 16px;
  color: #6a5570;
  font-size: 0.9em;
}

.sms-error {
  color: #a94442;
}

.sms-pending-opt-in,
.sms-scheduled {
  margin-top: 6px;
  font-size: 0.75em;
  font-weight: 600;
  -webkit-user-select: none;
  user-select: none;
}

.sms-pending-opt-in {
  color: #8a5a12;
}

.sms-scheduled {
  color: #3d5a80;
}

.sms-opt-in-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 16px;
}

.sms-opt-in-dialog {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  max-width: 320px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.sms-opt-in-gate-dialog {
  max-width: 380px;
  width: 100%;
}

.sms-opt-in-dialog p {
  margin: 0 0 12px;
  font-size: 0.92em;
  color: #333;
  line-height: 1.4;
}

.sms-opt-in-ok {
  display: block;
  margin-left: auto;
  border: 0;
  background: #a080a8;
  color: #fff;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
}

.sms-item {
  margin-bottom: 12px;
  cursor: pointer;
}

.sms-item-selected .sms-bubble {
  box-shadow: 0 0 0 2px #a080a8;
}

.sms-stack {
  max-width: 88%;
}

.sms-item-reply .sms-stack {
  margin-left: 20px;
}

.sms-item-out .sms-bubble {
  background: #f3eef6;
  border-color: #a080a8;
}

.sms-send-dot {
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-sizing: border-box;
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;
}

.sms-send-dot-pending {
  background: transparent;
  border: 1.5px solid #c8b7ce;
}

.sms-send-dot-sent {
  background: #9ee6a8;
  border: 0;
}

.sms-send-dot-failed {
  background: #f0a3a3;
  border: 0;
}

.sms-item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.sms-filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f3eef6;
  border-bottom: 1px solid #e0d6e4;
  flex-shrink: 0;
}

.sms-filter-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82em;
  color: #4a3a52;
}

.sms-filter-clear {
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #6a5570;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
}

.sms-from-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.sms-from {
  font-size: 0.72em;
  color: #1a73e8;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.sms-from-active {
  font-weight: 600;
}

.sms-create-contact {
  flex-shrink: 0;
  margin: 0;
  padding: 0 6px;
  border: 1px solid #1a73e8;
  border-radius: 3px;
  background: transparent;
  color: #1a73e8;
  font-size: 0.65em;
  line-height: 1.4;
  font-weight: 500;
  cursor: pointer;
}

.sms-unknown-name-input {
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 10px;
  padding: 8px 10px;
  border: 1px solid #d4c8d8;
  border-radius: 6px;
  font-size: 0.92em;
}

.sms-edit-field {
  display: block;
  margin: 0;
  font-size: 0.75em;
  color: #6a5570;
}

.sms-edit-optin {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px;
  font-size: 0.85em;
  color: #333;
}

.sms-ago {
  flex-shrink: 0;
  font-size: 0.72em;
  color: #8a7a90;
}

.sms-bubble {
  position: relative;
  background: #f3eef6;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  padding: 8px 16px 10px 10px;
  text-align: left;
}

.sms-in-reply {
  font-size: 0.72em;
  color: #8a7a90;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  -webkit-user-select: text;
  user-select: text;
  -webkit-touch-callout: default;
}

.sms-body {
  font-size: 0.92em;
  color: #333;
  line-height: 1.35;
  max-height: calc(1.35em * 2);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: pre-wrap;
  word-break: break-word;
  -webkit-user-select: text;
  user-select: text;
  -webkit-touch-callout: default;
}

.sms-body-expanded {
  display: block;
  max-height: none;
  -webkit-line-clamp: unset;
}

.sms-media {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 6px;
  object-fit: contain;
  border-radius: 4px;
}

.sms-retry-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #ddd;
  background: #faf7fb;
  color: #4a3751;
  font-size: 0.85em;
}

.sms-retry-label {
  min-width: 0;
}

.sms-retry-label .sms-error {
  display: block;
}

.sms-retry-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.sms-compose {
  flex-shrink: 0;
  border-top: 1px solid #ddd;
  background: #fff;
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px));
}

.sms-compose-to {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78em;
  color: #6a5570;
  margin-bottom: 6px;
}

.sms-compose-to-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sms-basic-once {
  color: #6e5875;
  font-weight: 600;
}

.sms-opt-in-edit,
.sms-compose-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: 1px solid #c9b8d0;
  border-radius: 50%;
  background: #f3eef6;
  color: #6e5875;
  cursor: pointer;
  padding: 0;
}

.sms-opt-in-gate-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c9b8d0;
  border-radius: 6px;
  padding: 8px;
  font: inherit;
  font-size: 0.9em;
  resize: vertical;
  margin-bottom: 8px;
}

.sms-opt-in-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sms-opt-in-actions-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sms-opt-in-basic {
  border: 1px solid #c9b8d0;
  background: #fff;
  color: #4a3751;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 600;
}

.sms-opt-in-cancel {
  border: 1px solid #c9b8d0;
  background: #fff;
  color: #4a3751;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 600;
}

.sms-compose-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c9b8d0;
  border-radius: 6px;
  padding: 8px;
  font: inherit;
  font-size: 1em;
  resize: none;
}

.sms-photo-preview {
  position: relative;
  display: inline-block;
  margin-top: 6px;
}

.sms-photo-preview img {
  display: block;
  max-width: 160px;
  max-height: 120px;
  border-radius: 6px;
  object-fit: cover;
}

.sms-photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(74, 55, 81, 0.82);
  color: #fff;
  cursor: pointer;
  padding: 0;
}

.sms-compose-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.sms-photo-tools {
  display: flex;
  gap: 6px;
  margin-right: auto;
  flex-shrink: 0;
}

.sms-photo-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #c9b8d0;
  border-radius: 8px;
  background: #faf7fb;
  color: #6e5875;
  cursor: pointer;
}

.sms-photo-btn-off {
  opacity: 0.45;
  pointer-events: none;
}

.sms-photo-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.sms-compose-actions .sms-error {
  flex: 1;
  min-width: 0;
  text-align: right;
}

.sms-send {
  border: none;
  background: #a080a8;
  color: #fff;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.sms-send-clock {
  font-size: 0.8em;
  margin-right: 6px;
}

.sms-schedule-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 8px;
  padding: 8px;
  border: 1px solid #c9b8d0;
  border-radius: 6px;
  font: inherit;
  color: #333;
}

.sms-send:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.sms-delete {
  background: #fff;
  color: #6e5875;
  border: 1px solid #c9b8d0;
}

.sms-search-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 16px;
}

.sms-search-popup {
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.sms-search-title {
  background: #6e5875;
  color: #fff;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}

.sms-search-close {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
}

.sms-search-close:hover {
  background: rgba(255, 255, 255, 0.22);
}

.sms-search-input {
  margin: 10px 12px 6px;
  padding: 8px 10px;
  font-size: 16px;
  border: 1px solid #c9b8d0;
  border-radius: 6px;
}

.sms-search-results {
  --sms-hit-h: 48px;
  --sms-hit-gap: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--sms-hit-gap);
  height: calc(var(--sms-hit-h) * 8 + var(--sms-hit-gap) * 7);
  overflow-y: auto;
  padding: 0 8px 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sms-search-results::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.sms-search-hit {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  width: 100%;
  height: var(--sms-hit-h, 48px);
  box-sizing: border-box;
  text-align: left;
  border: 1px solid #e0d6e4;
  background: #faf7fb;
  padding: 0 10px;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  line-height: 1.2;
}

.sms-search-hit-alt {
  background: #ede4f0;
}

.sms-search-hit:hover,
.sms-search-hit-alt:hover {
  background: #e3d4ec;
  border-color: #c9b3d2;
}

.sms-search-hit:active {
  background: #d8c4e6;
}

.sms-search-hit-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: #4a3a50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sms-new-number {
  margin: 8px 4px 4px;
  padding: 8px;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
  background: #faf7fb;
}

.sms-new-number-title {
  font-size: 0.85em;
  font-weight: 600;
  color: #4a3a52;
  margin-bottom: 6px;
}

.sms-new-number-name {
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 6px;
  padding: 8px 10px;
  border: 1px solid #d4c8d8;
  border-radius: 6px;
  font-size: 0.92em;
}

.sms-new-number .sms-search-hit {
  margin: 0;
}

.sms-search-hit-side {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  max-width: 58%;
  min-width: 0;
}

.sms-search-hit-meta {
  min-width: 0;
  font-size: 0.75em;
  color: #8a7a90;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sms-opt-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-sizing: border-box;
  flex-shrink: 0;
}

.sms-opt-dot-in {
  background: #3cba6a;
  border: 0;
}

.sms-opt-dot-empty {
  background: transparent;
  border: 1.5px solid #c8b7ce;
}

@keyframes pixie-push-highlight {
  0%,
  100% {
    background: transparent;
  }
  25%,
  75% {
    background: #efe6f3;
  }
}

.sms-item-push-highlight {
  animation: pixie-push-highlight 1.2s ease-in-out 2;
  border-radius: 8px;
}

.sms-item-push-highlight .sms-bubble {
  box-shadow: 0 0 0 2px #6e5875;
}
</style>
